import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '~components/button';
import Tippy from '~components/tippy';
import Typography from '~components/typography';
import { TickSmallError, TickSmallWarning } from '~static/svg-components';

const useStyles = makeStyles((theme) => ({
  root: {
    '&.tippy-box > .tippy-content': {
      padding: 16,
      maxWidth: 320,
    },
  },

  rootDanger: {
    '&.tippy-box': {
      backgroundColor: theme.colors.redBg,
      '&& > .tippy-arrow': {
        color: theme.colors.redBg,
      },
    },
  },

  content: {
    display: 'flex',
    '& > svg:first-child': { marginTop: 4, marginRight: 12 },
  },

  actions: {
    marginTop: 16,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    '& > *:not(:first-child)': {
      marginLeft: 16,
    },
  },
}));

const ConfirmationTippy = React.forwardRef((props, ref) => {
  const {
    type,
    content,
    contentHasIcon = true,
    confirmButtonProps,
    cancelButtonProps,
    onConfirm,
    onCancel,
    onCreate,
    children,
    className,
    contentChildren,
    cancelAutoHideTippyInstanceWhenClickConfirm,
    ...otherProps
  } = props;
  const { t } = useTranslation();
  const [instance, setInstance] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCreate = (arg) => {
    if (instance !== arg) {
      setInstance(arg);
    }
    if (onCreate) onCreate(arg);
  };

  const handleClickConfirmButton = async (e) => {
    e.stopPropagation();

    setLoading(true);
    try {
      if (onConfirm) {
        await onConfirm();
      }
    } finally {
      setLoading(false);
    }
    if (!instance.state.isDestroyed && !cancelAutoHideTippyInstanceWhenClickConfirm) {
      instance.hide();
    }
  };

  const handleClickCancelButton = async (e) => {
    e.stopPropagation();

    if (onCancel) {
      await onCancel();
    }
    if (!instance.state.isDestroyed) instance.hide();
  };

  const getConfirmButtonColor = () => {
    if (!type || type === 'default') {
      return 'primary';
    }
    if (type === 'danger') {
      return 'danger';
    }
    return undefined;
  };

  const classes = useStyles();
  return (
    <Tippy
      content={
        <>
          <div className={classes.content}>
            {contentHasIcon && (type === 'danger' ? <TickSmallError /> : <TickSmallWarning />)}
            {content && <Typography component="div">{content}</Typography>}
            {contentChildren}
          </div>

          <div className={classes.actions}>
            <Button
              variant="text"
              size="small"
              onClick={handleClickCancelButton}
              {...cancelButtonProps}
            >
              {cancelButtonProps?.children ?? t('button.cancel')}
            </Button>
            <Button
              size="small"
              color={getConfirmButtonColor()}
              loading={loading}
              {...confirmButtonProps}
              onClick={handleClickConfirmButton}
            />
          </div>
        </>
      }
      arrow
      theme="popper"
      placement="top-end"
      offset={[0, 12]}
      {...otherProps}
      ref={ref}
      onCreate={handleCreate}
      className={classNames(classes.root, { [classes.rootDanger]: type === 'danger' }, className)}
    >
      {children}
    </Tippy>
  );
});

ConfirmationTippy.propTypes = {
  type: PropTypes.oneOf(['default', 'danger']),
  content: PropTypes.node,
  contentHasIcon: PropTypes.bool,
  confirmButtonProps: PropTypes.object.isRequired,
  onConfirm: PropTypes.func,
  cancelButtonProps: PropTypes.object,
  onCancel: PropTypes.func,
  children: PropTypes.node.isRequired,
  onCreate: PropTypes.func,
  className: PropTypes.string,
  contentChildren: PropTypes.node,
  cancelAutoHideTippyInstanceWhenClickConfirm: PropTypes.bool,
};

ConfirmationTippy.defaultProps = {
  type: undefined,
  contentHasIcon: undefined,
  onConfirm: undefined,
  cancelButtonProps: undefined,
  onCancel: undefined,
  onCreate: undefined,
  className: '',
};

export default ConfirmationTippy;
